import { HqApiService } from 'hq/services/hqApiService';
import { OgpApiService } from 'ogp/services/ogp-api-service';
import { Site } from 'shared/hooks';
import { DEFAULT_PAGE_SIZE } from 'shared/utils/constants';
import {
  Companies,
  companiesSchema,
  companyEmploymentTypesSchema,
  GetCompaniesResponse,
  getCompaniesResponseSchema,
  GetCompanyEmploymentTypes,
  GetContractorCompanyRates,
  getContractorCompanyRatesSchema,
  UpdateContractorCompanyRate,
} from './company-service.schema';

import {
  CompanyEmploymentTypesResponseDto,
  GetCompaniesResponseDto,
  GetContractorCompanyRatesDto,
} from './company-service.dto';

type CompaniesProps = {
  site: Site;
  includeArchived: boolean;
  filter: string | undefined;
};

type GetCompaniesPageProps = CompaniesProps & {
  page: number;
  pageSize: number;
};

export class CompanyService {
  constructor(
    private readonly ogpApiService: OgpApiService,
    private readonly hqApiService: HqApiService
  ) {}

  public getCompanyEmploymentTypes = async (
    site: Site,
    companyId?: string
  ): Promise<GetCompanyEmploymentTypes> => {
    const companyEmploymentTypes = await this.getCompanyEmploymentTypesImpl(site, companyId);

    return companyEmploymentTypesSchema.parse(companyEmploymentTypes);
  };

  private async getCompanyEmploymentTypesImpl(
    site: Site,
    companyId?: string
  ): Promise<CompanyEmploymentTypesResponseDto> {
    if (site === 'hq') {
      return await this.hqApiService.get<CompanyEmploymentTypesResponseDto>({
        path: `companies/${companyId}/employment-types/`,
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.get<CompanyEmploymentTypesResponseDto>({
        path: `companies/employment-types/`,
      });
    }

    throw new Error('Not implemented');
  }

  public async getCompanies(props: CompaniesProps): Promise<Companies> {
    const allCompanies: Companies = [];
    let page = 0;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await this.getCompaniesPage({ ...props, page, pageSize: DEFAULT_PAGE_SIZE });
      allCompanies.push(...response.items);
      hasNextPage = response.hasNextPage;
      if (hasNextPage) {
        page = response.nextPage ?? page + 1;
      }
    }

    return companiesSchema.parse(allCompanies);
  }

  public async getCompaniesPage(props: GetCompaniesPageProps): Promise<GetCompaniesResponse> {
    const response = await this.getCompaniesPageImpl(props);

    return getCompaniesResponseSchema.parse(response);
  }

  private async getCompaniesPageImpl(
    props: GetCompaniesPageProps
  ): Promise<GetCompaniesResponseDto> {
    const { site, includeArchived, filter, page, pageSize } = props;
    if (site === 'hq') {
      return await this.hqApiService.get<GetCompaniesResponseDto>({
        params: { includeArchived: includeArchived ?? false, filter, page, pageSize },
        path: 'companies',
      });
    }
    throw new Error('Not implemented');
  }

  public async getContractorCompanyRates(): Promise<GetContractorCompanyRates> {
    const response = await this.ogpApiService.get<GetContractorCompanyRatesDto>({
      path: 'companies/employment-type-job-type-rates',
    });

    return getContractorCompanyRatesSchema.parse(response);
  }

  public async updateContractorCompanyRate(payload: UpdateContractorCompanyRate): Promise<void> {
    await this.ogpApiService.update<void>({
      path: 'companies/employment-type-job-type-rates',
      body: payload,
    });
  }
}
